<template>
  <div v-if="loading" class="page-content">
    <Loading />
  </div>
  <div v-if="isBack" @click="goBack" class="icon-button-container">
    <span class="material-symbols-outlined">arrow_back</span>
    {{ $t("Custom.back") }}
  </div>
  <div class="properties-strip">
    <div class="action-strip-buttons">
      <div class="dropdown" :class="{ open: isOpen }" ref="countryDropdown">
        <div class="caption" @click="toggleDropdown">
          <img
            :src="selectedCountryData?.countryFlagPath"
            alt=""
            width="32"
            height="auto"
            class="flag"
          />
          {{ this.selectedCountryData?.countryName || "Select a country" }}
          <span>{{ selectedCountryData?.countryShortName }}</span>
        </div>
        <div class="list">
          <div
            class="item"
            v-for="country in appCountries"
            :key="country.id"
            @click="selectItem(country)"
          >
            <img
              :src="country.countryFlagPath"
              :alt="country.countryShortName"
              width="32"
              height="17"
            />
            <span class="name">{{ country.countryName }} </span>
            <span class="short-name">{{ country.countryShortName }}</span>
          </div>
        </div>
      </div>
      <div
        class="dropdown"
        :class="{ open: isListMenuOpen }"
        ref="listDropdown"
      >
        <div class="caption" @click="toggleListMenu">
          <span class="material-icons-outlined list-icon"> playlist_add </span>
          {{ $t("List.addToList") }}
        </div>
        <div class="list">
          <div
            v-for="item in sortedAppList"
            :key="item.listId"
            class="checkbox-item"
            @click="toggleListSelection(item.listId)"
            :title="item.list.listName"
          >
            <div class="list-item">
              <input
                type="checkbox"
                :id="`list-${item.listId}`"
                :checked="isChecked(item.listId)"
              />
              <label :for="`list-${item.listId}`">
                {{ item.list.listName }}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div class="action-handle-buttons">
        <template v-if="appFromLibrary">
          <div
            class="icon-button-container"
            @click="handleAreYouSureModal"
            title="Remove From Library"
          >
            <template v-if="libraryLoading">
              <span class="button-spinner"></span>
            </template>
            <template v-else>
              <span class="material-symbols-outlined icon" style="color: green">
                bookmarks
              </span>
              <span style="margin-left: 5px; color: green"> Added </span>
            </template>
          </div>
        </template>
        <template v-else>
          <div
            class="icon-button-container"
            @click="handleAddToLibrary"
            title="Add To Library"
          >
            <template v-if="libraryLoading">
              <span class="button-spinner"></span>
            </template>
            <template v-else>
              <span class="material-symbols-outlined icon" style="color: red">
                bookmark_add
              </span>
              <span style="margin-left: 5px; color: red"> Add To Library </span>
            </template>
          </div>
        </template>

        <div class="icon-button-container" @click="updateNotification">
          <template v-if="isNotificationLoading">
            <span class="button-spinner"></span>
          </template>
          <template v-else>
            <span
              class="material-symbols-outlined icon"
              :style="
                appFromLibrary?.isNotification
                  ? { color: 'green' }
                  : { color: 'red' }
              "
            >
              {{
                appFromLibrary?.isNotification
                  ? "notifications_active"
                  : "notification_add"
              }}
            </span>
            <span
              style="margin-left: 5px"
              :style="
                appFromLibrary?.isNotification
                  ? { color: 'green' }
                  : { color: 'red' }
              "
            >
              {{ appFromLibrary?.isNotification ? "Is On" : "Is Off" }}
            </span>
          </template>
        </div>
      </div>
    </div>
  </div>

  <app-header
    :app="appData.app"
    :developer="appData.developer"
    :platformType="appData.platformType"
  />
  <tab-view
    :app-data="appData"
    :historyData="historyData"
    :defaultTab="defaultTab"
    :historyId="historyId"
    @showDeveloper="showDeveloper"
  />

  <NotificationComponent
    v-if="notification.show"
    :key="notification.key"
    :type="notification.type"
    :title="notification.title"
    :message="notification.message"
    @closed="!notification.show"
  />

  <AreYouSureModal
    v-if="showModal"
    :isVisible="showModal"
    :title="modalTitle"
    :message="modalMessage"
    :itemId="selectedAppId"
    :confirmButtonLabel="$t('Custom.remove')"
    :cancelButtonLabel="$t('Custom.cancel')"
    @result="handleModalResult"
  />
</template>

<script>
import { storeImage } from "@/ultis/user/functions/storePlatform";
import NotificationComponent from "@/components/custom/notifications/NotificationComponent.vue";
import AreYouSureModal from "@/components/custom/modals/AreYouSureModal.vue";
import DropdownMenu from "@/components/custom/dropdown/DropdownMenu.vue";
import Modal from "@/components/custom/modals/ModalComponent.vue";
import Loading from "@/components/user/layout/LoadingComponent.vue";
import AppHeader from "./tabs/AppHeader.vue";
import TabView from "./tabs/TabView.vue";
import clickOutside from "@/ultis/user/functions/click-outside";
import {
  addAppToList,
  getAppList,
  getSavedList,
  removeAppFromList,
} from "@/api/appList.api";
import { getAppHistory } from "@/api/appHistory.api";
import {
  addToLibrary,
  getAppFromLibrary,
  removeAppFromAll,
  updateNotification,
} from "@/api/appLibrary.api";

export default {
  name: "AppInfo",
  components: {
    NotificationComponent,
    DropdownMenu,
    Modal,
    Loading,
    AppHeader,
    TabView,
    AreYouSureModal,
  },
  directives: {
    clickOutside,
  },
  props: {
    appData: {
      type: Object,
      required: true,
    },
    appCountries: {
      type: Array,
      required: true,
    },
    // historyData: {
    //   type: Array,
    //   required: false,
    // },
    isBack: {
      type: Boolean,
      default: false,
    },
    defaultTab: {
      type: String,
      required: false,
    },
    historyId: {
      type: [String, Number],
      required: false,
    },
  },
  data() {
    return {
      notification: {
        show: false,
        key: 0,
        type: "",
        title: "",
        message: "",
      },
      appData: this.appData,
      appCountries: this.appCountries,
      modalTitle: "",
      modalMessage: "",
      isOpen: false,
      isListMenuOpen: false,
      libraryLoading: false,
      appList: null,
      appSavedList: null,
      selectedListIds: [],
      selectedCountryData: [],
      historyData: [],
      appList: [],
      loading: false,
      appFromLibrary: [],
      isNotificationLoading: false,
      appIsNotification: false,
      selectedScreenshot: null,
      showModal: false,
    };
  },
  async created() {
    this.loading = true;
    this.setDefaultSelectedCountry();
    await this.fetchHistories();
    await this.getAppFromLibrary();
    await this.fetchAppList();
    await this.getSavedList();
    this.loading = false;
  },
  computed: {
    hasSavedList() {
      return this.appSavedList && this.appSavedList.length > 0;
    },
    sortedAppList() {
      return [...this.appList].sort((a, b) => {
        const aSaved = this.isChecked(a.listId);
        const bSaved = this.isChecked(b.listId);
        if (aSaved !== bSaved) return aSaved - bSaved;
        return a.list.listName.localeCompare(b.list.listName);
      });
    },
  },
  watch: {
    appData: {
      handler(newVal) {
        appData;
      },
      deep: true,
    },
  },
  methods: {
    async handleAddToLibrary() {
      this.libraryLoading = true;
      try {
        const response = await addToLibrary(this.appData?.app?.appId);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.getAppFromLibrary();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.libraryLoading = false;
      }
    },
    async handleModalResult(result, appId) {
      this.showModal = false;
      if (result) {
        this.handleRemoveApp(appId);
      }
    },
    async handleRemoveApp(appId) {
      this.loading = true;
      try {
        const response = await removeAppFromAll(appId);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.getAppFromLibrary();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    handleAreYouSureModal() {
      this.modalTitle = this.$t("Custom.areYouSure");
      this.modalMessage =
        this.$t("Custom.doYouWantRemoveThis") +
        "<b> " +
        this.appData?.app?.appName +
        +this.appFromLibrary +
        "</b> ? " +
        this.$t("Custom.thisActionCannotBeUndone") +
        "<p style='color: red'>" +
        this.$t("Custom.deleteFromAllLists") +
        " (" +
        (this.appFromLibrary?.list?.length || 0) +
        ")" +
        "</p> ";
      this.selectedAppId = this.appData?.app?.appId;
      this.showModal = true;
    },
    goBack() {
      this.$emit("close");
    },
    async fetchHistories() {
      const payload =
        this.selectedCountryData.countryShortName === "US"
          ? {
              appId: this.appData.app.id,
              countryId: this.selectedCountryData.id,
            }
          : {
              appId: this.appData.app.appId,
              countryId: this.selectedCountryData.id,
            };
      try {
        const response = await getAppHistory(payload.appId, payload.countryId);
        if (response.data.status) {
          this.historyData = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    getStoreImage(platformTypeId) {
      return storeImage(platformTypeId);
    },
    async fetchAppList() {
      const payload =
        this.appData.country.countryShortName === "US"
          ? this.appData.app.id
          : this.appData.app.appId;

      try {
        const response = await getAppList(payload);

        if (response.data.status) {
          this.appList = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      } finally {
        this.loading = false;
      }
    },
    async getSavedList() {
      if (this.appSavedList && this.appSavedList.length > 0) {
        return;
      }

      const payload =
        this.appData.country.countryShortName === "US"
          ? this.appData.app.id
          : this.appData.app.appId;

      try {
        const response = await getSavedList(payload);
        if (response.data.status) {
          this.appSavedList = response.data.data;
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    toggleListSelection(listId) {
      const isChecked = this.isChecked(listId);
      this.setList(listId, !isChecked);
    },
    async setList(listId, isChecked) {
      this.loading = true;
      const payload =
        this.appData.country.countryShortName === "US"
          ? { appId: this.appData.app.id, listId: listId }
          : { appId: this.appData.app.appId, listId: listId };

      if (isChecked) {
        await this.addAppToList(payload);
      } else {
        await this.removeAppFromList(payload);
      }

      this.loading = false;
    },
    async addAppToList(payload) {
      try {
        const response = await addAppToList(payload.appId, payload.listId);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.getSavedList();
          // await this.fetchAppList();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          response.data.msg
        );
      }
    },
    async removeAppFromList(payload) {
      try {
        const response = await removeAppFromList(payload.appId, payload.listId);
        if (response.data.status) {
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
          await this.getSavedList();
          await this.fetchAppList();
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          error.response.data.msg
        );
      }
    },
    async updateNotification() {
      this.isNotificationLoading = true;
      const appIsNotification = this.appFromLibrary?.isNotification
        ? false
        : true;
      const payload =
        this.appData.country.countryShortName === "US"
          ? { appId: this.appData.app.id, isNotification: appIsNotification }
          : {
              appId: this.appData.app.appId,
              isNotification: appIsNotification,
            };
      try {
        const response = await updateNotification(
          payload.appId,
          payload.isNotification
        );
        if (response.data.status) {
          await this.getAppFromLibrary();
          this.showNotification(
            "success",
            this.$t("Custom.success"),
            response.data.msg
          );
        } else {
          this.showNotification(
            "error",
            this.$t("Custom.error"),
            response.data.msg
          );
        }
      } catch (error) {
        this.showNotification(
          "error",
          this.$t("Custom.error"),
          response.data.msg
        );
      } finally {
        this.isNotificationLoading = false;
      }
    },
    async getAppFromLibrary() {
      const payload =
        this.appData.country.countryShortName === "US"
          ? { appId: this.appData.app.id }
          : { appId: this.appData.app.appId };
      try {
        const response = await getAppFromLibrary(payload.appId);
        if (response.data.status) {
          this.appFromLibrary = response.data.data;
        } else {
          this.appFromLibrary = [];
        }
      } catch (error) {}
    },
    getObjectByListId(listId) {
      const foundObject = this.appSavedList.find(
        (item) => item.userListId === listId
      );
      return foundObject;
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    async selectItem(country) {
      this.isOpen = false;
      this.selectedCountryData = country;
      this.$router.replace({
        query: { 
          country: country.countryShortName.toUpperCase()
        },
      });
      await this.getApp();
    },
    async getApp() {
      await this.$emit("get-app", this.selectedCountryData.appUrl);
    },
    toggleListMenu() {
      this.isListMenuOpen = !this.isListMenuOpen;
    },
    setDefaultSelectedCountry() {
      const defaultCountry = this.appCountries.find(
        (country) => country.id === this.appData.country.id
      );
      if (defaultCountry) {
        this.selectedCountryData = defaultCountry;
      }
    },
    isChecked(listId) {
      if (!this.hasSavedList) return false;
      return this.appSavedList.some((target) => target.listId === listId);
    },
    showNotification(type, title, message) {
      this.notification = {
        show: true,
        key: Date.now(),
        type,
        title,
        message,
      };
    },
    handleClickOutside(event) {
      if (
        this.$refs.countryDropdown &&
        !this.$refs.countryDropdown.contains(event.target)
      ) {
        this.isOpen = false;
      }
      if (
        this.$refs.listDropdown &&
        !this.$refs.listDropdown.contains(event.target)
      ) {
        this.isListMenuOpen = false;
      }
    },
  },
  async mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
.app-info {
  position: relative;
  width: 100%;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 10px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 0 5px #9e9e9e;
  margin-bottom: 10px;
  font-size: 14px;
}

.back-button .material-icons-outlined {
  font-size: 18px;
  color: #333;
}

.back-button .text {
  font-weight: 500;
  color: #333;
}

.tracking-history {
  padding: 6px;
  max-height: 70vh;
  overflow-y: auto;
}

.tracking-history::-webkit-scrollbar {
  width: 6px;
}

.tracking-history::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 3px;
}

.tracking-history::-webkit-scrollbar-track {
  background-color: #f1f1f1;
}

.properties-strip {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 10px;
}

.material-icons-outlined {
  font-size: 15px;
}

/* Currency Switcher CSS */
.currency-switcher {
  position: relative;
  margin: 0 auto;
  width: 268px;
}
.flag {
  width: 25px;
}

div.dropdown {
  position: relative;
  width: 190px;
}

div.dropdown:after {
  content: "";
  position: absolute;
  margin: 0;
  width: 16px;
  height: 17px;
  background-image: url(http://botkits.ru/img/icons/down.svg);
  top: 15px;
  right: 10px;
  -webkit-transition: 0.3s;
  transition: 0.3s;
}

div.dropdown.open:after {
  -webkit-transform: rotate(180deg);
  transform: rotate(180deg);
}

div.dropdown > div.caption {
  background: #ffffff;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  padding: 9px 13px 9px 45px;
  font-size: 13px;
  line-height: 150%;
  letter-spacing: 0.3px;
  box-shadow: 0 0 5px #9e9e9e;
}

div.dropdown > div.list {
  position: absolute;
  background-color: #fff;
  width: 100%;
  top: 42px;
  border-radius: 8px;
  padding: 10px 0;
  opacity: 0;
  visibility: hidden;
  overflow: hidden;
  overflow-y: auto;
  max-height: 300px;
  box-shadow: 0px 12px 24px rgba(21, 18, 51, 0.13);
  transform: scale(0.85);
  transform-origin: 50% 0;
  transition: all 0.15s cubic-bezier(0.25, 0, 0.25, 1.75), opacity 0.1s linear;
  -webkit-transition: all 0.15s cubic-bezier(0.25, 0, 0.25, 1.75),
    opacity 0.1s linear;
  -webkit-box-shadow: 0px 12px 24px rgba(21, 18, 51, 0.13);
  -webkit-transform: scale(0.85);
  -webkit-transform-origin: 50% 0;
  z-index: -1;
}

div.dropdown > div.list::-webkit-scrollbar {
  width: 4px;
  cursor: pointer;
}

div.dropdown > div.list::-webkit-scrollbar-thumb {
  background-color: var(--logo-color);
  border-radius: 4px;
  cursor: pointer;
}

div.dropdown > div.list::-webkit-scrollbar-thumb:hover {
  background-color: var(--logo-hover-color);
  cursor: pointer;
}

div.dropdown.open > div.list {
  -webkit-transform: scale(1);
  transform: scale(1);
  opacity: 1;
  z-index: 1;
  visibility: visible;
}

div.dropdown > div.list > div.item {
  padding: 8.5px 15px 8.5px 50px;
  cursor: pointer;
  -webkit-transition: 0.3s;
  transition: 0.3s;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.3px;
}

.div.dropdown > div.list > div.item .name {
  font-size: 13px;
  letter-spacing: 0.3px;
  user-select: none;
  font-weight: 400;
}

div.dropdown > div.list > div.item.selected {
  background: rgba(36, 60, 187, 0.2);
  pointer-events: none;
}

div.dropdown > div.list > div.item:hover {
  background: #f8f9fb;
}

div.dropdown > div.caption img,
div.dropdown > div.list > div.item img,
div.dropdown > div.caption svg,
div.dropdown > div.list > div.item svg {
  position: absolute;
  margin-top: 2.5px;
  left: 15px;
}

div.dropdown > div.list > div.item .short-name,
div.dropdown > div.caption span {
  font-weight: 600;
  font-size: 10px;
  letter-spacing: 0.3px;
  color: var(--logo-color);
  position: absolute;
  right: 20px;
}

div.dropdown > div.list > div.item .short-name {
  right: 12px;
}
div.dropdown > div.caption .list-icon {
  font-size: 18px;
  font-weight: 400;
  line-height: 1;
  color: var(--logo-color);
  transition: transform 0.2s ease;
}

/* List Menu Styles */
.list-menu {
  margin-top: 10px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 0 5px #9e9e9e;
  padding: 10px;
  max-width: 300px;
  width: 100%;
}

.checkbox-item {
  display: flex;
  align-items: center;
  margin-bottom: 0px;
  padding: 0px 15px;
}

.checkbox-item input[type="checkbox"] {
  appearance: none;
  width: 16px;
  height: 16px;
  border: 1px solid #bdbdbd;
  border-radius: 4px;
  display: inline-block;
  margin-right: 5px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  background-color: white;
}

.checkbox-item input[type="checkbox"]:hover,
.checkbox-item:hover input[type="checkbox"] {
  border-color: var(--logo-color);
  box-shadow: 0 0 5px rgba(243, 177, 33, 0.4);
}

.checkbox-item input[type="checkbox"]:checked {
  border-color: var(--logo-color);
  background-color: var(--logo-color);
}

.checkbox-item input[type="checkbox"]:checked::after {
  content: "\2713";
  font-size: 14px;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.checkbox-item label {
  font-size: 14px;
  margin-left: 2px;
  color: #333;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100% - 40px);
}

.checkbox-item:hover {
  cursor: pointer;
}

.list-item {
  display: flex;
  align-items: center;
  min-width: 100%;
  padding: 8px 0px;
  margin-bottom: 5px;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
}
@media (max-width: 855px) {
  div.dropdown {
    position: relative;
    width: 100%;
  }
}
</style>
